import React, { useEffect, useRef, useState } from "react";
import { CalendarClock, History, Mail, RefreshCw, Send } from "lucide-react";
import api from "../api/client";
import { useToast } from "../components/ToastProvider";

const PAGE_SIZE = 25;

function formatSchedule(value) {
  if (!value) return "—";
  const date = new Date(`${value.replace(" ", "T")}+08:00`);
  return date.toLocaleString("en-AU", {
    timeZone: "Australia/Perth", day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function formatSent(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-AU", {
    timeZone: "Australia/Perth", day: "numeric", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function ReminderStatus({ row }) {
  if (row.status === "sent") return <span className="rs-pill rs-pill--success">Sent</span>;
  if (row.status === "missed") return <span className="rs-pill rs-pill--danger">Not sent · overdue</span>;
  if (row.status === "due") return <span className="rs-pill rs-pill--warning">Not sent · due now</span>;
  const days = row.daysUntil;
  const label = days === 0 ? "Sending alert today" : days === 1 ? "Sending alert tomorrow"
    : `Sending alert in ${days} days`;
  return <span className="rs-pill rs-pill--info">{label}</span>;
}

export default function PaymentReminders() {
  const toast = useToast();
  const [tab, setTab] = useState("upcoming");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const activeSends = useRef(new Set());

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const customerName = search.trim().replace(/\s+/g, " ");
    const load = () => {
      api.get("/api/admin/payment-reminders", {
        params: { tab, page, limit: PAGE_SIZE, ...(customerName ? { search: customerName } : {}) },
      })
        .then((response) => {
          if (!active) return;
          const next = response.data;
          if (page > 1 && next.data.length === 0) {
            setPage(Math.max(1, Math.ceil(next.total / PAGE_SIZE)));
          } else {
            setResult(next);
          }
        })
        .catch((err) => {
          if (active) setError(err?.response?.data?.message || "Failed to load payment reminders");
        })
        .finally(() => { if (active) setLoading(false); });
    };
    const timer = customerName ? setTimeout(load, 300) : null;
    if (!customerName) load();
    return () => { active = false; clearTimeout(timer); };
  }, [tab, page, refreshKey, search]);

  const sendReminder = async (row) => {
    if (activeSends.current.has(row.serviceId)) return;
    activeSends.current.add(row.serviceId);
    setSending((prev) => ({ ...prev, [row.serviceId]: true }));
    try {
      await api.post(`/api/admin/payment-reminders/${row.serviceId}/send`, {}, { timeout: 90000 });
      toast.success(`Payment reminder sent to ${row.email}.`);
      setRefreshKey((key) => key + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not confirm sending. Refresh before trying again.");
    } finally {
      activeSends.current.delete(row.serviceId);
      setSending((prev) => ({ ...prev, [row.serviceId]: false }));
    }
  };

  const changeTab = (next) => { setTab(next); setPage(1); };
  const history = tab === "history";
  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));

  return (
    <div className="rs-page">
      <div className="rs-page__header">
        <div>
          <h1 className="rs-h1">Payment Reminder Mails</h1>
          <div className="rs-muted">Track recurring payment alerts and send an email whenever needed.</div>
        </div>
        <button className="rs-btn rs-btn--outline" disabled={loading} onClick={() => setRefreshKey((key) => key + 1)}>
          <RefreshCw size={16} /> {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="rs-card rs-reminder-note">
        <Mail size={22} aria-hidden="true" />
        <div>
          <div className="rs-card__title">One place for every payment reminder</div>
          <div className="rs-card__subtitle">
            Alerts are scheduled 36 hours before the next clean. Weekly: 7 days · Fortnightly: 14 days · Monthly: 28 days.
            All dates and times are in Perth (AWST).
          </div>
          <div className="rs-card__subtitle">Manual emails use the automatic reminder template and payment link. Sent emails can be sent again.</div>
        </div>
      </div>

      <div className="rs-tabs rs-reminder-tabs" role="group" aria-label="Reminder views">
        <button type="button" className={`rs-tab ${!history ? "is-active" : ""}`} aria-pressed={!history} onClick={() => changeTab("upcoming")}>
          <CalendarClock size={18} /> Today & upcoming
        </button>
        <button type="button" className={`rs-tab ${history ? "is-active" : ""}`} aria-pressed={history} onClick={() => changeTab("history")}>
          <History size={18} /> History
        </button>
      </div>

      <section className="rs-card" aria-label={history ? "Reminder history" : "Upcoming reminders"} aria-busy={loading}>
        <div className="rs-card__header">
          <div className="rs-card__title">{history ? "Send history & missed alerts" : "Payment alerts for the next 30 days"}</div>
          <div className="rs-card__subtitle">
            {history ? "Latest records first. Overdue alerts without a successful send are included for recovery."
              : "Today first, then tomorrow and onwards. Each paid recurring booking schedules its next reminder."}
          </div>
        </div>

        <div className="rs-reminder-search">
          <label className="rs-field">
            <span>Search by customer name</span>
            <input type="search" className="rs-input" placeholder="Enter first name, last name or full name"
              maxLength={200} value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
          </label>
          {search && <button type="button" className="rs-btn rs-btn--outline"
            onClick={() => { setSearch(""); setPage(1); }}>Clear search</button>}
        </div>

        {error ? <div className="rs-form-error" role="alert">{error} <button className="rs-btn rs-btn--ghost" onClick={() => setRefreshKey((key) => key + 1)}>Try again</button></div>
          : loading ? <div className="rs-empty rs-empty--panel" role="status">Loading reminders...</div>
            : <>
              <div className="rs-table-wrap">
                <table className="rs-table rs-reminder-table">
                  <thead><tr>
                    <th>Booking ID</th>{history ? <th>Mail ID</th> : null}<th>Customer</th><th>Frequency</th><th>Alert scheduled</th>
                    <th>Next clean</th><th>Status</th><th>{history ? "Sent at" : "Last sent"}</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {result.data.length === 0 ? <tr><td colSpan={history ? 9 : 8} className="rs-empty">
                      {search.trim() ? "No reminders found for this customer name."
                        : history ? "No reminder history or missed alerts." : "No payment alerts scheduled in the next 30 days."}
                    </td></tr> : result.data.map((row) => <tr key={row.id}>
                      <td data-label="Booking ID"><div className="rs-td__value">{row.serviceId ?? "—"}</div></td>
                      {history ? <td data-label="Mail ID"><div className="rs-td__value">{row.historyId ?? "—"}</div></td> : null}
                      <td data-label="Customer"><div className="rs-td__value rs-cell--stack">
                        <div className="rs-reminder-customer">{`${row.firstName || ""} ${row.lastName || ""}`.trim() || "Booking unavailable"}</div>
                        <div className="rs-muted">{row.email || "No email address"}</div>
                      </div></td>
                      <td data-label="Frequency"><div className="rs-td__value rs-capitalize">{row.frequency || "—"}</div></td>
                      <td data-label="Alert scheduled"><div className="rs-td__value">{formatSchedule(row.reminderAt)}</div></td>
                      <td data-label="Next clean"><div className="rs-td__value">{formatSchedule(row.nextCleaningAt)}</div></td>
                      <td data-label="Status"><div className="rs-td__value"><ReminderStatus row={row} /></div></td>
                      <td data-label={history ? "Sent at" : "Last sent"}><div className="rs-td__value">{formatSent(row.sentAt)}</div></td>
                      <td data-label="Action"><div className="rs-td__value">
                        <button type="button" className="rs-btn rs-btn--primary rs-reminder-send"
                          disabled={!row.canSend || sending[row.serviceId]}
                          onClick={() => sendReminder(row)}
                          title={row.canSend ? `Send payment reminder to ${row.email}` : "A paid recurring booking with an email address is required"}>
                          <Send size={15} /> {sending[row.serviceId] ? "Sending..." : row.status === "sent" ? "Send again" : "Send email"}
                        </button>
                      </div></td>
                    </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="rs-pagination">
                <div className="rs-muted">{result.total} {history ? "records" : "reminders"} · Page {page} of {totalPages}</div>
                <div className="rs-actions">
                  <button className="rs-btn rs-btn--outline" disabled={page === 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
                  <button className="rs-btn rs-btn--outline" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>Next</button>
                </div>
              </div>
            </>}
      </section>
    </div>
  );
}
